/**
 * CV upload status — one row per file: Reading → Uploading (real %, from the browser) → Processing
 * CV & evaluating (the server is extracting text and scoring it) → Completed (with the score) or
 * Failed (with the server's own reason). Used by the Add Candidate modal and the review page's
 * Add / Replace CV, so both show the same states. Nothing here is simulated: the upload % is the
 * bytes actually sent, and "Completed" only appears when the server answered.
 */
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, FileText, Loader2 } from "lucide-react";

export type CvUploadPhase = "reading" | "uploading" | "processing" | "done" | "failed";

export interface CvUploadItem {
  id: string;
  name: string;
  phase: CvUploadPhase;
  /** 0..1 while uploading. */
  progress: number;
  /** Outcome / error line. */
  message?: string;
  score?: number | null;
}

export const ACCEPTED_CV = /\.(pdf|docx?)$/i;
export const MAX_CV_BYTES = 10 * 1024 * 1024;

/** A file the server can't take, said before anything is sent. */
export function cvProblem(file: File): string | null {
  if (!ACCEPTED_CV.test(file.name)) return "Only PDF or Word (.doc, .docx) files.";
  if (file.size > MAX_CV_BYTES) return "Over 10 MB — compress or re-save it, then try again.";
  if (file.size === 0) return "The file is empty.";
  return null;
}

const LABEL: Record<CvUploadPhase, string> = {
  reading: "Reading file…",
  uploading: "Uploading",
  processing: "Processing CV — extracting and evaluating…",
  done: "Completed",
  failed: "Failed",
};

export function CvUploadStatus({ items, evaluating = true }: { items: CvUploadItem[]; evaluating?: boolean }): ReactNode {
  if (items.length === 0) return null;
  return (
    <ul className="t1-cvup" aria-live="polite">
      {items.map((it) => {
        const busy = it.phase === "reading" || it.phase === "uploading" || it.phase === "processing";
        const label = it.phase === "processing" && !evaluating ? "Processing CV — extracting text…" : LABEL[it.phase];
        return (
          <li key={it.id} className={`t1-cvup-item ${it.phase}`}>
            <span className="t1-cvup-ic" aria-hidden="true">
              {it.phase === "done" ? <CheckCircle2 /> : it.phase === "failed" ? <AlertTriangle /> : busy ? <Loader2 className="spin" /> : <FileText />}
            </span>
            <div className="t1-cvup-body">
              <div className="t1-cvup-top">
                <b title={it.name}>{it.name}</b>
                <span className="t1-cvup-phase">
                  {label}
                  {it.phase === "uploading" ? ` ${Math.round(it.progress * 100)}%` : ""}
                  {it.phase === "done" && it.score != null ? ` · ${Math.round(it.score)}% match` : ""}
                </span>
              </div>
              {(it.phase === "uploading" || it.phase === "reading") && (
                <div className="t1-cvup-bar">
                  <i style={{ width: `${Math.round(it.progress * 100)}%` }} />
                </div>
              )}
              {it.phase === "processing" && (
                <div className="t1-cvup-bar indeterminate">
                  <i />
                </div>
              )}
              {it.message && <div className="t1-cvup-msg">{it.message}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
